import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Filter, X, Save, RefreshCw, Users, MapPin, Briefcase, 
  Languages, Award, Calendar, Activity, Star, Zap, Brain
} from 'lucide-react';

interface AdvancedFilterOptions {
  availability: 'all' | 'online' | 'offline' | 'busy';
  joinedDate: 'all' | 'week' | 'month' | 'year';
  activityLevel: 'all' | 'high' | 'medium' | 'low';
  compatibilityScore: number[];
  subscriptionType: 'all' | 'basic' | 'premium' | 'enterprise';
  hasPortfolio: boolean;
  hasCalendar: boolean;
  isRecommended: boolean;
  distance: number[];
  commonConnections: boolean;
  // Existing filters
  country: string;
  sector: string;
  role: string;
  language: string;
  skill: string;
}

interface AdvancedMemberFiltersProps {
  isOpen: boolean;
  filters: AdvancedFilterOptions;
  onFiltersChange: (filters: AdvancedFilterOptions) => void;
  onSaveFilters: (name: string) => void;
  onLoadFilters: (filters: AdvancedFilterOptions) => void;
  onResetFilters: () => void;
  filterValues: {
    countries: string[];
    sectors: string[];
    roles: string[];
    languages: string[];
    skills: string[];
  };
  savedFilters: Array<{ name: string; filters: AdvancedFilterOptions }>;
}

export const AdvancedMemberFilters: React.FC<AdvancedMemberFiltersProps> = ({
  isOpen,
  filters,
  onFiltersChange,
  onSaveFilters,
  onLoadFilters,
  onResetFilters,
  filterValues,
  savedFilters
}) => {
  if (!isOpen) return null;

  const updateFilter = (key: keyof AdvancedFilterOptions, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'compatibilityScore' || key === 'distance') {
      return Array.isArray(value) && (value[0] > 0 || value[1] < 100);
    }
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value !== '' && value !== 'all';
    return false;
  }).length;

  return (
    <Card className="border-l-4 border-l-primary">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            Filtres Avancés
            {activeFiltersCount > 0 && (
              <Badge variant="default" className="ml-2">
                {activeFiltersCount}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onSaveFilters('Nouvelle recherche')}>
              <Save className="h-4 w-4 mr-1" />
              Sauvegarder
            </Button>
            <Button variant="outline" size="sm" onClick={onResetFilters}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Availability & Activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Activity className="h-4 w-4 text-green-600" />
              Disponibilité
            </label>
            <Select value={filters.availability} onValueChange={(value) => updateFilter('availability', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="online">En ligne</SelectItem>
                <SelectItem value="offline">Hors ligne</SelectItem>
                <SelectItem value="busy">Occupé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="h-4 w-4 text-blue-600" />
              Date d'inscription
            </label>
            <Select value={filters.joinedDate} onValueChange={(value) => updateFilter('joinedDate', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les dates</SelectItem>
                <SelectItem value="week">Cette semaine</SelectItem>
                <SelectItem value="month">Ce mois</SelectItem>
                <SelectItem value="year">Cette année</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Zap className="h-4 w-4 text-yellow-600" />
              Niveau d'activité
            </label>
            <Select value={filters.activityLevel} onValueChange={(value) => updateFilter('activityLevel', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les niveaux</SelectItem>
                <SelectItem value="high">Très actif</SelectItem>
                <SelectItem value="medium">Modéré</SelectItem>
                <SelectItem value="low">Faible</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* AI Compatibility Score */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Brain className="h-4 w-4 text-purple-600" />
            Score de Compatibilité IA
            <Badge variant="outline" className="ml-2 bg-purple-50 text-purple-700 border-purple-200">
              {filters.compatibilityScore[0]}% - {filters.compatibilityScore[1]}%
            </Badge>
          </label>
          <Slider
            value={filters.compatibilityScore}
            onValueChange={(value) => updateFilter('compatibilityScore', value)}
            max={100}
            min={0}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Faible compatibilité</span>
            <span>Haute compatibilité</span>
          </div>
        </div>

        {/* Location & Distance */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="h-4 w-4 text-red-600" />
              Pays
            </label>
            <Select value={filters.country} onValueChange={(value) => updateFilter('country', value === 'all' ? '' : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Tous les pays" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les pays</SelectItem>
                {filterValues.countries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="h-4 w-4 text-red-600" />
              Distance (km)
              <Badge variant="outline" className="ml-2">
                {filters.distance[0]} - {filters.distance[1]} km
              </Badge>
            </label>
            <Slider
              value={filters.distance}
              onValueChange={(value) => updateFilter('distance', value)}
              max={1000}
              min={0}
              step={10}
              className="w-full"
            />
          </div>
        </div>

        {/* Professional Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Briefcase className="h-4 w-4 text-blue-600" />
              Secteur
            </label>
            <Select value={filters.sector} onValueChange={(value) => updateFilter('sector', value === 'all' ? '' : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Tous les secteurs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les secteurs</SelectItem>
                {filterValues.sectors.map((sector) => (
                  <SelectItem key={sector} value={sector}>
                    {sector}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Award className="h-4 w-4 text-yellow-600" />
              Rôle
            </label>
            <Select value={filters.role} onValueChange={(value) => updateFilter('role', value === 'all' ? '' : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Tous les rôles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                {filterValues.roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Star className="h-4 w-4 text-purple-600" />
              Abonnement
            </label>
            <Select value={filters.subscriptionType} onValueChange={(value) => updateFilter('subscriptionType', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Skills & Languages */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Languages className="h-4 w-4 text-green-600" />
              Langue
            </label>
            <Select value={filters.language} onValueChange={(value) => updateFilter('language', value === 'all' ? '' : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Toutes les langues" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les langues</SelectItem>
                {filterValues.languages.map((language) => (
                  <SelectItem key={language} value={language}>
                    {language}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Zap className="h-4 w-4 text-orange-600" />
              Compétence
            </label>
            <Select value={filters.skill} onValueChange={(value) => updateFilter('skill', value === 'all' ? '' : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Toutes les compétences" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les compétences</SelectItem>
                {filterValues.skills.map((skill) => (
                  <SelectItem key={skill} value={skill}>
                    {skill}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Boolean Toggles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Portfolio disponible</label>
            <Switch 
              checked={filters.hasPortfolio} 
              onCheckedChange={(value) => updateFilter('hasPortfolio', value)} 
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Calendrier ouvert</label>
            <Switch 
              checked={filters.hasCalendar} 
              onCheckedChange={(value) => updateFilter('hasCalendar', value)} 
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Recommandé par l'IA</label>
            <Switch 
              checked={filters.isRecommended} 
              onCheckedChange={(value) => updateFilter('isRecommended', value)} 
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Connexions communes</label>
            <Switch 
              checked={filters.commonConnections} 
              onCheckedChange={(value) => updateFilter('commonConnections', value)} 
            />
          </div>
        </div>

        {/* Saved Filters */}
        {savedFilters.length > 0 && (
          <div className="space-y-2 pt-4 border-t">
            <label className="text-sm font-medium">Recherches sauvegardées</label>
            <div className="flex flex-wrap gap-2">
              {savedFilters.map((saved, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => onLoadFilters(saved.filters)}
                  className="text-xs"
                >
                  {saved.name}
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
